import axios, { AxiosInstance, AxiosError } from 'axios';
import { firebaseAuth } from '../config/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach a fresh Firebase ID token to every outgoing request. Firebase caches
// tokens for an hour and auto-refreshes, so getIdToken() is cheap unless it
// needs to fetch a new one.
api.interceptors.request.use(
  async (config) => {
    const user = firebaseAuth.currentUser;
    if (user) {
      try {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      } catch (err) {
        console.warn('Failed to fetch Firebase ID token:', err);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401: sign the user out locally and bounce them to /login.
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      firebaseAuth.signOut().catch(() => {
        /* ignore */
      });
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.response?.data?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}
